/**
 * PDF report, ported from loader-export.js exportPDF: three 792x612 canvas
 * pages (summary, loading matrix, conflict log when there are conflicts) drawn
 * with the same layout and colours, encoded as JPEG (quality 0.92) and
 * assembled into a PDF 1.4 file by hand (catalog, pages, one page + image
 * XObject + content stream per page, cross-reference table, trailer).
 *
 * The drawing functions take a minimal 2D context so they run against a stub
 * in tests; the assembly is pure bytes.
 */
import { toast } from '@/components/ui/toast';
import type { AnalysisResults } from '../analysis/types';
import type { Antenna, Mission } from '../types';
import { exportFilename, snapshotFromStore, type ExportSnapshot } from './csv';
import { downloadBlob } from './download';

/** Letter landscape, in points. */
export const PAGE_W = 792;
export const PAGE_H = 612;
export const MARGIN = 40;
export const JPEG_QUALITY = 0.92;
export const PDF_MIME = 'application/pdf';

const BACKGROUND = '#1c1f23';
const ACCENT = '#35b1cd';
const MUTED = '#8899aa';
const TEXT = '#cccccc';
const GREEN = '#4CAF50';
const ORANGE = '#FF9800';
const RED = '#F44336';

/** The subset of CanvasRenderingContext2D the report pages use. */
export interface PageContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  font: string;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
}

export interface ReportModel {
  network: string;
  results: AnalysisResults;
  /** Active missions in priority order. */
  missions: Mission[];
  /** Active antennas in dataset order. */
  antennas: Antenna[];
  /** Every dataset mission and antenna, for names in the conflict log. */
  missionById: Map<string, Mission>;
  antennaById: Map<string, Antenna>;
  generatedAtMs: number;
}

export function reportModel(s: ExportSnapshot, nowMs: number = Date.now()): ReportModel {
  return {
    network: s.network,
    results: s.results,
    missions: s.missions.slice().sort((a, b) => a.priorityTier - b.priorityTier),
    antennas: s.antennas,
    missionById: new Map(s.dataset.missions.map((m) => [m.id, m])),
    antennaById: new Map(s.dataset.antennas.map((a) => [a.id, a])),
    generatedAtMs: nowMs,
  };
}

function slaColor(pct: number): string {
  return pct >= 95 ? GREEN : pct >= 70 ? ORANGE : RED;
}

function utilColor(pct: number): string {
  return pct > 90 ? RED : pct > 70 ? ORANGE : GREEN;
}

function fillBackground(ctx: PageContext): void {
  ctx.fillStyle = BACKGROUND;
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);
}

/** Page 1: title, subtitle, divider, four count boxes, the overall SLA bar and the first 19 missions' SLA. */
export function drawSummaryPage(ctx: PageContext, model: ReportModel): void {
  const { results, missions } = model;
  fillBackground(ctx);

  ctx.fillStyle = ACCENT;
  ctx.font = 'bold 24px sans-serif';
  ctx.fillText('Network Loading Analysis Report', MARGIN, MARGIN + 24);

  ctx.fillStyle = MUTED;
  ctx.font = '12px sans-serif';
  ctx.fillText(
    `${model.network.toUpperCase()} Network | ${new Date(model.generatedAtMs).toLocaleDateString()} | ${results.summary.missionCount} Missions, ${results.summary.antennaCount} Antennas`,
    MARGIN,
    MARGIN + 48,
  );

  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(MARGIN, MARGIN + 60);
  ctx.lineTo(PAGE_W - MARGIN, MARGIN + 60);
  ctx.stroke();

  const boxY = MARGIN + 80;
  const boxW = 160;
  const boxH = 80;
  const boxes = [
    { label: 'Missions', value: results.summary.missionCount, color: '#2196F3' },
    { label: 'Antennas', value: results.summary.antennaCount, color: GREEN },
    { label: 'Conflicts', value: results.summary.totalConflicts, color: ORANGE },
    { label: 'Resolved', value: results.summary.resolvedConflicts, color: '#8BC34A' },
  ];
  boxes.forEach((box, i) => {
    const x = MARGIN + i * (boxW + 15);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fillRect(x, boxY, boxW, boxH);
    ctx.strokeStyle = box.color;
    ctx.lineWidth = 1;
    ctx.strokeRect(x, boxY, boxW, boxH);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 28px sans-serif';
    ctx.fillText(String(box.value), x + 15, boxY + 40);
    ctx.fillStyle = MUTED;
    ctx.font = '12px sans-serif';
    ctx.fillText(box.label, x + 15, boxY + 62);
  });

  const slaY = boxY + boxH + 30;
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.fillText(`Overall SLA: ${results.summary.overallSLA}%`, MARGIN, slaY);

  const barX = MARGIN;
  const barY = slaY + 10;
  const barW = PAGE_W - 2 * MARGIN;
  const barH = 20;
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.fillRect(barX, barY, barW, barH);
  ctx.fillStyle = slaColor(results.summary.overallSLA);
  ctx.fillRect(barX, barY, barW * (results.summary.overallSLA / 100), barH);

  let tableY = barY + barH + 40;
  ctx.fillStyle = ACCENT;
  ctx.font = 'bold 14px sans-serif';
  ctx.fillText('Mission SLA Fulfillment', MARGIN, tableY);

  tableY += 20;
  ctx.fillStyle = MUTED;
  ctx.font = '11px sans-serif';
  ctx.fillText('Mission', MARGIN, tableY);
  ctx.fillText('Priority', MARGIN + 150, tableY);
  ctx.fillText('Minutes', MARGIN + 220, tableY);
  ctx.fillText('Passes', MARGIN + 300, tableY);
  ctx.fillText('SLA %', MARGIN + 370, tableY);

  missions.forEach((m, i) => {
    if (i > 18) return;
    const y = tableY + 18 + i * 18;
    const sla = results.missionSLA[m.id];
    ctx.fillStyle = TEXT;
    ctx.fillText(m.shortName, MARGIN, y);
    ctx.fillText(`P${m.priorityTier}`, MARGIN + 150, y);
    ctx.fillText(String(sla?.totalMinutes || 0), MARGIN + 220, y);
    ctx.fillText(String(sla?.passes || 0), MARGIN + 300, y);
    const pct = sla?.percentage || 0;
    ctx.fillStyle = slaColor(pct);
    ctx.fillText(`${pct}%`, MARGIN + 370, y);
  });
}

/** Page 2: the loading matrix (first 29 missions, columns that fit the page) with heat cells, SLA and a utilisation row. */
export function drawMatrixPage(ctx: PageContext, model: ReportModel): void {
  const { results, missions, antennas } = model;
  fillBackground(ctx);

  ctx.fillStyle = ACCENT;
  ctx.font = 'bold 18px sans-serif';
  ctx.fillText('Loading Matrix', MARGIN, MARGIN + 18);

  const colW = Math.min(90, (PAGE_W - MARGIN * 2 - 120) / (antennas.length + 1));
  const rowH = 18;
  const startX = MARGIN + 120;
  const startY = MARGIN + 40;

  ctx.fillStyle = MUTED;
  ctx.font = '10px sans-serif';
  ctx.fillText('Mission', MARGIN, startY);
  antennas.forEach((a, i) => {
    if (i * colW + startX > PAGE_W - MARGIN) return;
    ctx.fillText(a.shortName, startX + i * colW, startY);
  });
  ctx.fillText('SLA', startX + antennas.length * colW + 5, startY);

  missions.forEach((m, ri) => {
    if (ri > 28) return;
    const y = startY + (ri + 1) * rowH;
    const sla = results.missionSLA[m.id];

    ctx.fillStyle = TEXT;
    ctx.font = '10px sans-serif';
    ctx.fillText(`${m.shortName} [P${m.priorityTier}]`, MARGIN, y);

    antennas.forEach((a, ci) => {
      if (ci * colW + startX > PAGE_W - MARGIN) return;
      const minutes = sla?.perAntenna?.[a.id]?.minutes ?? 0;
      if (minutes > 0) {
        const intensity = Math.min(1, minutes / 500);
        ctx.fillStyle = `rgba(33, 150, 243, ${0.1 + intensity * 0.4})`;
        ctx.fillRect(startX + ci * colW - 2, y - 12, colW - 2, rowH - 2);
      }
      ctx.fillStyle = minutes > 0 ? '#ffffff' : '#555555';
      ctx.fillText(minutes > 0 ? String(minutes) : '-', startX + ci * colW, y);
    });

    const pct = sla?.percentage || 0;
    ctx.fillStyle = slaColor(pct);
    ctx.fillText(`${pct}%`, startX + antennas.length * colW + 5, y);
  });

  const utilY = startY + (Math.min(missions.length, 29) + 2) * rowH;
  ctx.fillStyle = ACCENT;
  ctx.font = 'bold 10px sans-serif';
  ctx.fillText('Utilization', MARGIN, utilY);
  antennas.forEach((a, ci) => {
    if (ci * colW + startX > PAGE_W - MARGIN) return;
    const pct = results.antennaUtil[a.id]?.percentage ?? 0;
    ctx.fillStyle = utilColor(pct);
    ctx.fillText(`${pct}%`, startX + ci * colW, utilY);
  });
}

/** Page 3: the first 31 conflicts with their resolution strategy, data capture and a 35-character description. */
export function drawConflictPage(ctx: PageContext, model: ReportModel): void {
  const { results, missionById, antennaById } = model;
  fillBackground(ctx);

  ctx.fillStyle = ACCENT;
  ctx.font = 'bold 18px sans-serif';
  ctx.fillText(`Conflict Log (${results.conflicts.length} conflicts)`, MARGIN, MARGIN + 18);

  let y = MARGIN + 50;
  ctx.fillStyle = MUTED;
  ctx.font = '10px sans-serif';
  ctx.fillText('Mission A', MARGIN, y);
  ctx.fillText('Mission B', MARGIN + 120, y);
  ctx.fillText('Antenna', MARGIN + 240, y);
  ctx.fillText('Strategy', MARGIN + 340, y);
  ctx.fillText('Capture', MARGIN + 480, y);
  ctx.fillText('Description', MARGIN + 540, y);

  results.conflicts.forEach((conflict, idx) => {
    if (idx > 30) return;
    y += 18;
    const res = results.resolutions[idx];
    ctx.fillStyle = TEXT;
    ctx.font = '10px sans-serif';
    ctx.fillText(missionById.get(conflict.missionA)?.shortName ?? conflict.missionA, MARGIN, y);
    ctx.fillText(missionById.get(conflict.missionB)?.shortName ?? conflict.missionB, MARGIN + 120, y);
    ctx.fillText(antennaById.get(conflict.antennaId)?.shortName ?? conflict.antennaId, MARGIN + 240, y);
    ctx.fillStyle = res?.strategy === 'UNRESOLVED' ? RED : GREEN;
    ctx.fillText(res?.strategy ?? 'N/A', MARGIN + 340, y);
    ctx.fillStyle = TEXT;
    // dataCapture is a fraction (0..1) in the ported results.
    ctx.fillText(`${Math.round((res?.dataCapture || 0) * 100)}%`, MARGIN + 480, y);
    let desc = res?.description ?? '';
    if (desc.length > 35) desc = desc.substring(0, 35) + '...';
    ctx.fillText(desc, MARGIN + 540, y);
  });
}

export interface JpegPage {
  jpeg: Uint8Array;
  width: number;
  height: number;
}

const encoder = new TextEncoder();
const ascii = (s: string) => encoder.encode(s);

/**
 * A PDF 1.4 file from JPEG pages. Object numbering: 1 catalog, 2 pages, then
 * per page p: 3+3p the page, 4+3p its image XObject (DCTDecode), 5+3p its
 * content stream drawing the image over the whole MediaBox. The page objects
 * come first in the file, then image and content pairs, as the original wrote
 * them; the cross-reference table is in object order with byte offsets.
 */
export function assemblePdf(pages: JpegPage[]): Uint8Array<ArrayBuffer> {
  const parts: Uint8Array[] = [];
  let length = 0;
  const offsets: number[] = [];
  const push = (bytes: Uint8Array) => {
    parts.push(bytes);
    length += bytes.length;
  };
  const beginObject = (objNum: number) => {
    offsets[objNum - 1] = length;
  };

  push(ascii('%PDF-1.4\n'));
  // Binary-file marker comment: '%' followed by four bytes above 127.
  push(new Uint8Array([0x25, 0xc0, 0xc1, 0xc2, 0xc3, 0x0a]));

  beginObject(1);
  push(ascii('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n'));

  const kids = pages.map((_, i) => `${3 + i * 3} 0 R`).join(' ');
  beginObject(2);
  push(ascii(`2 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`));

  pages.forEach((_, i) => {
    const pageObj = 3 + i * 3;
    const imgObj = 4 + i * 3;
    const contentObj = 5 + i * 3;
    beginObject(pageObj);
    push(
      ascii(
        `${pageObj} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents ${contentObj} 0 R /Resources << /XObject << /Img${i} ${imgObj} 0 R >> >> >>\nendobj\n`,
      ),
    );
  });

  pages.forEach((page, i) => {
    const imgObj = 4 + i * 3;
    const contentObj = 5 + i * 3;
    beginObject(imgObj);
    push(
      ascii(
        `${imgObj} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>\nstream\n`,
      ),
    );
    push(page.jpeg);
    push(ascii('\nendstream\nendobj\n'));

    const content = `q ${PAGE_W} 0 0 ${PAGE_H} 0 0 cm /Img${i} Do Q`;
    beginObject(contentObj);
    push(ascii(`${contentObj} 0 obj\n<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj\n`));
  });

  const xrefOffset = length;
  const count = offsets.length;
  let xref = `xref\n0 ${count + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) xref += `${String(offset).padStart(10, '0')} 00000 n \n`;
  xref += `trailer\n<< /Size ${count + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  push(ascii(xref));

  const out = new Uint8Array(length);
  let cursor = 0;
  for (const part of parts) {
    out.set(part, cursor);
    cursor += part.length;
  }
  return out;
}

export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** The canvas as JPEG bytes (quality 0.92, as the original). */
export function canvasToJpeg(canvas: HTMLCanvasElement, quality: number = JPEG_QUALITY): JpegPage {
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const base64 = dataUrl.split(',')[1] ?? '';
  if (!base64) throw new Error('Canvas produced no JPEG data');
  return { jpeg: base64ToBytes(base64), width: canvas.width, height: canvas.height };
}

export type CanvasFactory = () => HTMLCanvasElement;

const domCanvas: CanvasFactory = () => document.createElement('canvas');

/** The report's page painters in order: summary, matrix, and the conflict log when there are conflicts. */
export function reportPagePainters(model: ReportModel): ((ctx: PageContext) => void)[] {
  const painters = [(ctx: PageContext) => drawSummaryPage(ctx, model), (ctx: PageContext) => drawMatrixPage(ctx, model)];
  if (model.results.conflicts.length > 0) painters.push((ctx: PageContext) => drawConflictPage(ctx, model));
  return painters;
}

/** Draws every page on a fresh 792x612 canvas and assembles the PDF. */
export function buildPdfBytes(model: ReportModel, createCanvas: CanvasFactory = domCanvas): Uint8Array<ArrayBuffer> {
  const pages = reportPagePainters(model).map((paint) => {
    const canvas = createCanvas();
    canvas.width = PAGE_W;
    canvas.height = PAGE_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context is unavailable');
    paint(ctx);
    return canvasToJpeg(canvas);
  });
  return assemblePdf(pages);
}

/** Downloads network-loading-<network>-<date>.pdf for the current analysis; warns when there is none. */
export function exportPdf(): boolean {
  const snapshot = snapshotFromStore();
  if (!snapshot) return false;
  try {
    const bytes = buildPdfBytes(reportModel(snapshot));
    downloadBlob(bytes, exportFilename(snapshot.network, null, 'pdf'), PDF_MIME);
    return true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[export] PDF export failed:', err);
    toast('error', `PDF export failed: ${message}`);
    return false;
  }
}
